"use client";

import { motion } from "framer-motion";
import { type LucideIcon, Mail, Phone, PhoneOutgoing, Timer, Voicemail } from "lucide-react";
import { useRef } from "react";
import { cn } from "@/lib/utils";
import { BAR_COUNT, generatePlaceholderHeights } from "@/lib/waveform-utils";
import { Avatar } from "@/ui/elements";
import { EASE } from "../../constants";
import { useStickToBottom } from "../../use-stick-to-bottom";
import { BRAND_LOGO, type LeadStatus, MARISSA, type Offer, type ThreadItem } from "./data";
import { StatusText } from "./status";

function Bubble({
  direction,
  children,
}: {
  direction: "incoming" | "outgoing";
  children: React.ReactNode;
}) {
  const out = direction === "outgoing";
  return (
    <div className={cn("w-full", out ? "text-right" : "text-left")}>
      <div
        className={cn(
          "inline-block max-w-[15rem] whitespace-pre-line rounded-2xl border px-3 py-2 text-left text-[13px] leading-snug sm:max-w-[17rem]",
          out
            ? "rounded-br-md border-transparent bg-outgoing-bubble text-white"
            : "rounded-bl-md border-border bg-incoming-bubble text-foreground",
        )}
      >
        {children}
      </div>
    </div>
  );
}

function OfferCard({ offer }: { offer: Offer }) {
  return (
    <div className="mt-1.5 flex justify-end">
      <div className="w-[15rem] rounded-xl border border-border bg-background p-3 text-left shadow-sm sm:w-[17rem]">
        <div className="flex items-center gap-1.5">
          <img
            src={BRAND_LOGO}
            alt={offer.brand}
            className="h-4 w-4 shrink-0 rounded-[4px] object-cover"
          />
          <span className="text-[10px] font-bold uppercase tracking-wide text-foreground">
            {offer.brand}
          </span>
        </div>
        <p className="mt-1.5 text-sm font-semibold text-foreground">{offer.title}</p>
        <p className="text-xs text-muted-foreground">{offer.detail}</p>
        <span className="mt-2 inline-block rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground">
          {offer.cta}
        </span>
      </div>
    </div>
  );
}

function EventChip({ icon: Icon, title, sub }: { icon: LucideIcon; title: string; sub?: string }) {
  return (
    <div className="flex justify-center">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] shadow-sm">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="font-medium text-foreground">{title}</span>
        {sub ? <span className="text-muted-foreground">· {sub}</span> : null}
      </span>
    </div>
  );
}

function SystemLine({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
      <Timer className="h-3 w-3" />
      <span>{text}</span>
    </div>
  );
}

function CallCard({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="flex w-full justify-end">
      <div className="inline-flex max-w-[17rem] items-center gap-2.5 rounded-2xl rounded-br-md border border-border bg-background px-3 py-2 text-left shadow-sm">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
          <PhoneOutgoing className="h-4 w-4 text-foreground" />
        </span>
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-foreground">{title}</p>
          <p className="text-[11px] text-muted-foreground">{sub}</p>
        </div>
      </div>
    </div>
  );
}

// Reuse the app's waveform generator so the bars read like a real voicemail
// (dense, varied, bell-curve envelope) rather than a sparse decorative row.
const WAVE = generatePlaceholderHeights("marissa-voicemail", BAR_COUNT);

function VoicemailCard({ duration, transcript }: { duration: string; transcript: string }) {
  return (
    <div className="w-full text-left">
      <div className="inline-block max-w-[17rem] rounded-2xl rounded-bl-md border border-border bg-incoming-bubble px-3 py-2.5 text-left">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-background">
            <Voicemail className="h-3.5 w-3.5 text-foreground" />
          </span>
          <div className="flex h-6 flex-1 items-center gap-[1.5px]">
            {WAVE.map((h, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: static decorative bars
                key={i}
                className="min-w-[2px] flex-1 rounded-full bg-foreground/25"
                style={{ height: `${h * 100}%` }}
              />
            ))}
          </div>
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {duration}
          </span>
        </div>
        <p className="mt-2 text-[13px] leading-snug text-foreground">
          <span className="text-muted-foreground">Transcript · </span>“{transcript}”
        </p>
      </div>
    </div>
  );
}

function Row({ item }: { item: ThreadItem }) {
  switch (item.kind) {
    case "event":
      return <EventChip icon={item.icon} title={item.title} sub={item.sub} />;
    case "system":
      return <SystemLine text={item.text} />;
    case "call":
      return <CallCard title={item.title} sub={item.sub} />;
    case "voicemail":
      return <VoicemailCard duration={item.duration} transcript={item.transcript} />;
    case "message":
      return (
        <div>
          <Bubble direction={item.direction}>{item.body}</Bubble>
          {item.offer ? <OfferCard offer={item.offer} /> : null}
        </div>
      );
    default:
      return null;
  }
}

export function Thread({
  items,
  visible,
  status,
  reduce,
}: {
  items: ThreadItem[];
  visible: number;
  status: LeadStatus;
  reduce: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the latest revealed item in view by scrolling ONLY this container —
  // never scrollIntoView, which would also yank the whole page. Backs off if the
  // user scrolls up, resumes when they return to the bottom.
  useStickToBottom(scrollRef, visible, reduce);

  const shown = items.slice(0, visible);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Avatar name={MARISSA.name} src={MARISSA.src} size="xs" />
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium leading-tight text-foreground">
            {MARISSA.name}
          </p>
          <StatusText status={status} reduce={reduce} />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted/60 sm:px-2.5 sm:py-1"
          >
            <Mail className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Send email</span>
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted/60 sm:px-2.5 sm:py-1"
          >
            <Phone className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Call</span>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-3 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {shown.map((item) => (
          <motion.div
            key={item.id}
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <Row item={item} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
